// src/hooks/useAnalytics.js
import { useState, useEffect, useCallback } from 'react';
import { db, auth } from '../firebase';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  serverTimestamp 
} from 'firebase/firestore';

const useAnalytics = () => {
  const [userId, setUserId] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Generate unique session ID
  const generateSessionId = useCallback(() => {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Save game session data
  const saveSessionData = useCallback(async (sessionData) => {
    if (!userId) return false;

    try {
      const sessionWithId = {
        ...sessionData,
        sessionId: generateSessionId(),
        timestamp: new Date().toISOString(),
        userId
      };

      // Save to user's sessions subcollection
      await addDoc(collection(db, 'users', userId, 'sessions'), sessionWithId);

      // Update user's analytics summary
      await updateUserAnalytics(sessionData);

      return true;
    } catch (error) {
      console.error('Error saving session data:', error);
      setError(error);
      return false;
    }
  }, [userId, generateSessionId]);

  // Update user analytics summary
  const updateUserAnalytics = useCallback(async (sessionData) => {
    if (!userId) return;

    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        console.log('❌ User document does not exist');
        return;
      }

      const currentData = userDoc.data();
      const currentAnalytics = currentData.analytics || {};

      console.log('🔍 BEFORE UPDATE:');
      console.log('Current analytics:', currentAnalytics);
      console.log('Session data to add:', sessionData);

      const updatedAnalytics = {
        totalPlayTime: (currentAnalytics.totalPlayTime || 0) + (sessionData.sessionDuration || 0),
        sessionsCount: (currentAnalytics.sessionsCount || 0) + 1,
        lastActive: new Date().toISOString(),
        totalScore: (currentAnalytics.totalScore || 0) + (sessionData.totalScore || 0),
        totalAttempts: (currentAnalytics.totalAttempts || 0) + (sessionData.attempts || 0),
        totalCorrect: (currentAnalytics.totalCorrect || 0) + (sessionData.correctAnswers || 0)
      };

      // Calculate learning streak
      const today = new Date().toDateString();
      const lastActiveDate = currentAnalytics.lastActive ? 
        new Date(currentAnalytics.lastActive).toDateString() : null;
      
      if (lastActiveDate === today) {
        updatedAnalytics.learningStreak = currentAnalytics.learningStreak || 1;
      } else if (lastActiveDate === new Date(Date.now() - 86400000).toDateString()) {
        updatedAnalytics.learningStreak = (currentAnalytics.learningStreak || 0) + 1;
      } else {
        updatedAnalytics.learningStreak = 1;
      }

      console.log('🔍 AFTER UPDATE:');
      console.log('Updated analytics:', updatedAnalytics);

      await updateDoc(userRef, {
        analytics: updatedAnalytics
      });

      console.log('✅ Analytics updated successfully');

    } catch (error) {
      console.error('❌ Error updating user analytics:', error);
    }
  }, [userId]);

  // Get dashboard data
  const getDashboardData = useCallback(async () => {
    if (!userId) return null;

    try {
      setIsLoading(true);
      setError(null);

      // Get user document with analytics
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data();
      const analytics = userData.analytics || {};
      const progress = userData.progress || {};

      // Get recent sessions (last 7 days)
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      const sessionsQuery = query(
        collection(db, 'users', userId, 'sessions'),
        where('timestamp', '>=', sevenDaysAgo.toISOString()),
        orderBy('timestamp', 'desc'),
        limit(50)
      );

      const sessionsSnapshot = await getDocs(sessionsQuery);
      const recentSessions = [];
      sessionsSnapshot.forEach(doc => {
        recentSessions.push({ id: doc.id, ...doc.data() });
      });

      // Calculate dashboard metrics
      const dashboardMetrics = {
        // Overview stats
        totalPlayTime: analytics.totalPlayTime || 0,
        sessionsCount: analytics.sessionsCount || 0,
        learningStreak: analytics.learningStreak || 0,
        totalScore: analytics.totalScore || 0,
        overallAccuracy: analytics.totalAttempts > 0 ? 
          Math.round((analytics.totalCorrect / analytics.totalAttempts) * 100) : 0,

        // Recent activity
        recentSessions,
        last7DaysTime: recentSessions.reduce((total, session) => 
          total + (session.sessionDuration || 0), 0),
        last7DaysSessions: recentSessions.length,

        // Game progress
        gamesProgress: Object.keys(progress).map(gameType => {
          const gameData = progress[gameType];
          const levels = gameData.levels || {};
          const completedLevels = Object.keys(levels).length;
          const totalStars = Object.values(levels).reduce((sum, level) => 
            sum + (level.bestStars || level.stars || 0), 0);

          return {
            gameType,
            currentLevel: gameData.currentLevel || 1,
            maxLevel: gameData.maxLevel || 1,
            completedLevels,
            totalStars,
            averageAccuracy: completedLevels > 0 ? 
              Math.round(Object.values(levels).reduce((sum, level) => 
                sum + (level.bestAccuracy || level.accuracy || 0), 0) / completedLevels) : 0
          };
        }),

        // Daily activity (last 7 days)
        dailyActivity: calculateDailyActivity(recentSessions),

        // Performance trends
        performanceTrend: calculatePerformanceTrend(recentSessions)
      };

      setDashboardData(dashboardMetrics);
      return dashboardMetrics;

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError(error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // Add this new function to recalculate analytics from all sessions
  const syncAnalyticsFromSessions = useCallback(async () => {
    if (!userId) return false;

    try {
      console.log('🔄 Syncing analytics from all sessions...');
      
      // Get ALL sessions
      const allSessionsQuery = query(
        collection(db, 'users', userId, 'sessions'),
        orderBy('timestamp', 'desc')
      );

      const allSessionsSnapshot = await getDocs(allSessionsQuery);
      const allSessions = [];
      allSessionsSnapshot.forEach(doc => {
        allSessions.push({ id: doc.id, ...doc.data() });
      });

      console.log(`Found ${allSessions.length} total sessions`);

      // Calculate totals from all sessions
      const totalPlayTime = allSessions.reduce((sum, session) => sum + (session.sessionDuration || 0), 0);
      const totalScore = allSessions.reduce((sum, session) => sum + (session.totalScore || 0), 0);
      const totalAttempts = allSessions.reduce((sum, session) => sum + (session.attempts || 0), 0);
      const totalCorrect = allSessions.reduce((sum, session) => sum + (session.correctAnswers || 0), 0);

      // Get the latest session date for learning streak calculation
      const latestSession = allSessions[0]; // Already sorted by timestamp desc
      const lastActive = latestSession ? latestSession.timestamp : new Date().toISOString();

      // Calculate learning streak properly
      const uniqueDays = [...new Set(allSessions.map(s => new Date(s.timestamp).toDateString()))];
      const today = new Date().toDateString();
      const yesterday = new Date(Date.now() - 86400000).toDateString();
      
      let learningStreak = 0;
      if (uniqueDays.includes(today)) {
        learningStreak = 1;
        // Count consecutive days backwards
        for (let i = 1; i < uniqueDays.length; i++) {
          const checkDate = new Date(Date.now() - i * 86400000).toDateString();
          if (uniqueDays.includes(checkDate)) {
            learningStreak++;
          } else {
            break;
          }
        }
      }

      const syncedAnalytics = {
        totalPlayTime,
        sessionsCount: allSessions.length,
        lastActive,
        totalScore,
        totalAttempts,
        totalCorrect,
        learningStreak,
        lastSynced: new Date().toISOString()
      };

      console.log('🔄 Synced analytics:', syncedAnalytics);

      // Update the analytics
      await updateDoc(doc(db, 'users', userId), {
        analytics: syncedAnalytics
      });

      console.log('✅ Analytics synced successfully');
      return true;

    } catch (error) {
      console.error('❌ Error syncing analytics:', error);
      return false;
    }
  }, [userId]);

  // Calculate daily activity for the last 7 days
  const calculateDailyActivity = useCallback((sessions) => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
      const dateString = date.toDateString();
      
      const daySessions = sessions.filter(session => 
        new Date(session.timestamp).toDateString() === dateString
      );

      days.push({
        date: dateString,
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        sessions: daySessions.length,
        totalTime: daySessions.reduce((sum, session) => sum + (session.sessionDuration || 0), 0),
        totalScore: daySessions.reduce((sum, session) => sum + (session.totalScore || 0), 0)
      });
    }
    return days;
  }, []);

  // Calculate performance trend
  const calculatePerformanceTrend = useCallback((sessions) => {
    if (sessions.length < 2) return 'stable';

    const recent = sessions.slice(0, Math.floor(sessions.length / 2));
    const older = sessions.slice(Math.floor(sessions.length / 2));

    const recentAvgAccuracy = recent.reduce((sum, s) => sum + (s.accuracy || 0), 0) / recent.length;
    const olderAvgAccuracy = older.reduce((sum, s) => sum + (s.accuracy || 0), 0) / older.length;

    const difference = recentAvgAccuracy - olderAvgAccuracy;

    if (difference > 5) return 'improving';
    if (difference < -5) return 'declining';
    return 'stable';
  }, []);

  // Auth state listener
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
        setDashboardData(null);
      }
    });
    return () => unsubscribe();
  }, []);

  return {
    // Data
    dashboardData,
    isLoading,
    error,

    // Actions
    saveSessionData,
    getDashboardData,
    generateSessionId,

    // Utilities
    userId
  };
};

export default useAnalytics;